import { useCallback, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { toast } from "react-toastify";
import Switch from "@mui/material/Switch";
import { queryStringBuilder } from "@client/utils";
import Service from "./service";
import UserPermissionsView from "./view";

const defaultState = {
  entries: [],
  totalEntries: 0,
  isLoading: false,
  filters: [],
  isFiltering: false,
  pageSize: 5,
  pageNumber: 1,
  order: null,
  orderBy: null,
  loaderConfig: {
    rows:[]
  },
};

const UserPermissions = () => {
  const [state, setState] = useState(defaultState);

	const fetchEntries = useCallback(async (
		pageSize = 5,
		pageNumber = 1,
		order = null,
		orderBy = "",
		filters = [],
    showLoader = true,
	) => {
    if (showLoader) {
      setState((prevState) => ({
        ...prevState,
        isLoading: true,
      }));
    }

    const updatedFilters = filters.map((filter) => {
      const subFields = (
        filterConfig.find((item) => item.field === filter.field) || {}
      ).subFields;
      if (subFields) {
        filter.subFields = subFields;
      }
      return filter;
    });

		let queryString = queryStringBuilder(
			pageSize,
			(pageNumber - 1) * pageSize,
			[],
			updatedFilters,
			{ field: orderBy, order: order }
		);

		const { data, error } = await Service.get(queryString);
    setState((prevState) => {
      let updatedState = {};
      if (showLoader) {
        updatedState = {
          ...updatedState,
          isLoading: false,
        }
      }
      if (error) {
        toast.error(Array.isArray(error) ? error[0]?.message : error);
      } else {
        updatedState = {
          ...updatedState,
          entries: data?.account_roles || defaultState.entries,
				  totalEntries: data?.count || defaultState.totalEntries,
        }
      }
      return {
        ...prevState,
        ...updatedState
      }
    });
	}, []);

	const handlePatch = async (id, payload, rowIndex) => {
    setState((prevState) => ({
      ...prevState,
      loaderConfig: {
        ...prevState.loaderConfig,
        rows: [...(prevState.loaderConfig?.rows || []), rowIndex]
      }
    }));
    const { error } = await Service.patch(id, payload);
    if (error) {
      toast.error(Array.isArray(error) ? error[0]?.message : error);
    } else {
      await fetchEntries(
        state.pageSize,
        state.pageNumber,
        state.order,
        state.orderBy,
        state.filters,
        false
      );
      toast.success("Updated Successfully");
    }
    setState((prevState) => ({
      ...prevState,
      loaderConfig: {
        ...prevState.loaderConfig,
        rows: (prevState.loaderConfig?.rows || []).filter((row)=> row != rowIndex)
      }
    }));
	};

  const handlePageNumberChange = useCallback((pageNumber) => {
		setState((prevState) => ({
			...prevState,
			pageNumber,
		}));
	}, []);

	const handlePageSizeChange = useCallback((pageSize) => {
		setState((prevState) => ({
			...prevState,
			pageSize,
			pageNumber: 1,
		}));
  }, []);

  const handleSort = useCallback((fieldObj, order) => {
    setState((prevState) => ({
      ...prevState,
      order,
      orderBy: order ? fieldObj.field || fieldObj.fieldName : null,
    }));
  }, []);

  const handleFilterChange = useCallback((filters = []) => {
    setState((prevState) => ({
      ...prevState,
      filters,
      isFiltering: !filters.length,
      pageNumber: defaultState.pageNumber
    }));
  }, []);

  const handleFilterClose = useCallback(() => {
    setState((prevState) => ({
			...prevState,
			isFiltering: false,
		}));
  }, []);

	const columns = [
		{
			id: "role_name",
			label: "Role Name",
      fieldName: "role_name",
			canSort: true,
			headerClassName: { width: "30%" },
			render: row => (
				<Tooltip title={row.role_name} placement="top-start">
					<Typography noWrap>{row.role_name}</Typography>
				</Tooltip>
			),
		},
    {
			id: "app_access",
			label: "App Access",
			render: (row, rowIndex) => (
				<Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Switch
            disabled={+row.role_id === 1}
            checked={row.app_access}
            onChange={evt => {
              const { checked } = evt.target;
              handlePatch(row.id, { appAccess: checked }, rowIndex);
            }}
          />
          <Typography variant="body2">{row.app_access ? 'Enabled' : 'Disabled'}</Typography>
        </Box>
			),
		},
	];

  const filterConfig = [
    {
      field: "role_name",
      fieldToDisplay: "Role Name",
      operatorType: "string"
    },
  ]

  useEffect(() => {
    fetchEntries(
      state.pageSize,
      state.pageNumber,
      state.order,
      state.orderBy,
      state.filters,
    );
  }, [
    state.pageNumber,
    state.pageSize,
    state.order,
    state.orderBy,
    state.filters,
  ]);

	return (
    <UserPermissionsView
      columns={columns}
      entries={state.entries}
      totalEntries={state.totalEntries}
      isLoading={state.isLoading}
      loaderConfig ={state.loaderConfig}
      pageNumber={state.pageNumber}
      pageSize={state.pageSize}
      order={state.order}
      orderBy={state.orderBy}
      filters={state.filters}
      filterConfig={filterConfig}
      isFiltering={state.isFiltering}
      handlePageNumberChange={handlePageNumberChange}
      handlePageSizeChange={handlePageSizeChange}
      handleSort={handleSort}
      handleFilterChange={handleFilterChange}
      handleFilterClose={handleFilterClose}
    />
	);
};

export default UserPermissions;
